import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { incidentsApi, type Incident, type IncidentOpsQueuesResponse } from '../services/incidentsApi';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability } from '../auth/access';
import { readNavContext } from '../utils/navContext';
import { buildScopedIncidentsPath } from '../utils/incidentsNavigation';

const PAGE_TIMEOUT_MS = 10000;

function withTimeout<T>(promise: Promise<T>, label: string, timeoutMs: number = PAGE_TIMEOUT_MS): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

function pickTopIncident(queues: IncidentOpsQueuesResponse | null | undefined): Incident | null {
  const q: any = queues?.queues || {};
  const order = ['escalations', 'overdue', 'unassigned_high_risk', 'my_queue'] as const;
  for (const key of order) {
    const items = Array.isArray(q?.[key]?.items) ? q[key].items : [];
    if (items.length > 0) return items[0] as Incident;
  }
  return null;
}

function buildResumePath(input: {
  queues: IncidentOpsQueuesResponse | null;
  lastIncidentId: string;
  navContext: ReturnType<typeof readNavContext>;
}): string {
  const topIncident = pickTopIncident(input.queues);
  if (topIncident?.incident_id) {
    return buildScopedIncidentsPath(
      `/incidents?incident_id=${encodeURIComponent(topIncident.incident_id)}&review=1`,
      { navContext: input.navContext }
    );
  }

  const q: any = input.queues?.queues || {};
  if (Number(q?.escalations?.count || 0) > 0) {
    return buildScopedIncidentsPath('/incidents?ops_queue=escalations', { navContext: input.navContext });
  }
  if (Number(q?.overdue?.count || 0) > 0) {
    return buildScopedIncidentsPath('/incidents?ops_queue=overdue', { navContext: input.navContext });
  }
  if (Number(q?.unassigned_high_risk?.count || 0) > 0) {
    return buildScopedIncidentsPath('/incidents?ops_queue=unassigned_high_risk', { navContext: input.navContext });
  }
  if (Number(q?.my_queue?.count || 0) > 0) {
    return buildScopedIncidentsPath('/incidents?ops_queue=my_queue', { navContext: input.navContext });
  }
  if (input.lastIncidentId) {
    return buildScopedIncidentsPath(
      `/incidents?incident_id=${encodeURIComponent(input.lastIncidentId)}&review=1`,
      { navContext: input.navContext }
    );
  }
  return buildScopedIncidentsPath('/incidents', { navContext: input.navContext });
}

function isCaseFirstRole(role: unknown): boolean {
  const normalized = String(role || '').trim().toLowerCase();
  return ['ehs_manager', 'sandbox_ehs_evaluator', 'customer_admin', 'site_admin', 'pilot_site_admin'].includes(normalized);
}

export default function LiveOpsHome() {
  const navigate = useNavigate();
  const { effectiveUser } = useAuth();
  const navContext = useMemo(() => readNavContext(), []);
  const lastIncidentId = String((navContext as any)?.incident_id || '').trim();
  const capabilities = Array.isArray((effectiveUser as any)?.capabilities) ? (effectiveUser as any).capabilities : [];
  const canReadIncidents = hasCapability(capabilities, 'incidents:read');
  const canReadAudit = hasCapability(capabilities, 'audit:read');
  const username = String((effectiveUser as any)?.username || '').trim() || undefined;
  const role = String((effectiveUser as any)?.role || '').trim().toLowerCase();

  useEffect(() => {
    let mounted = true;

    const go = async () => {
      if (!canReadIncidents) {
        navigate(canReadAudit ? '/audit' : '/overview', { replace: true });
        return;
      }

      if (isCaseFirstRole(role)) {
        navigate(
          buildScopedIncidentsPath('/incidents?table_mode=grouped', { navContext }),
          { replace: true }
        );
        return;
      }

      const queues = await withTimeout(
        incidentsApi.getOpsQueues({ assignee: username, limit: 1 }),
        'Ops queues'
      ).catch(() => null);

      if (!mounted) return;
      navigate(
        buildResumePath({
          queues,
          lastIncidentId,
          navContext,
        }),
        { replace: true }
      );
    };

    void go();

    return () => {
      mounted = false;
    };
  }, [canReadAudit, canReadIncidents, lastIncidentId, navContext, navigate, role, username]);

  return (
    <Box
      sx={{
        minHeight: 240,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
      }}
    >
      <CircularProgress size={28} />
      <Typography variant="h6">{isCaseFirstRole(role) ? 'Opening cases' : 'Opening incidents'}</Typography>
      <Typography variant="body2" color="text.secondary">
        {isCaseFirstRole(role)
          ? 'Redirecting to the case-first review queue.'
          : 'Redirecting to the best available incident queue context.'}
      </Typography>
    </Box>
  );
}
